import React, { useEffect, useState, useCallback } from "react";

import { Card, Col, Row, Button, Spinner } from "reactstrap";
import "../../assets/scss/style.css";
import { getAllBrokers } from "services/services";
import Pagination, { bootstrap5PaginationPreset } from "react-responsive-pagination";
import { useParams, useHistory } from "react-router-dom";
import { dateformat } from "admin/commonFunction";

const BrokerProperties = () => {
  const { brokerId } = useParams();
  const history = useHistory();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  // Data & loading states
  const [loading, setLoading] = useState(false);
  const [listingData, setListingData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch brokers and filter properties for current broker, then paginate locally
  const fetchData = useCallback(
    async () => {
      try {
        setLoading(true);
        console.log("🔍 Looking for broker ID:", brokerId);

        let broker = null;

        // Strategy 1: Try using broker ID as search filter
        try {
          console.log("Strategy 1: Trying search filter with broker ID...");
          const searchRes = await getAllBrokers(
            1,
            50,
            brokerId, // Use broker ID as search filter
            "", // No from date
            "", // No to date
            "", // No block filter
            "", // No status filter
            "false"
          );

          console.log("Search response:", searchRes);
          const searchPayload = searchRes?.data ?? searchRes;
          let searchItems = [];
          if (Array.isArray(searchPayload)) {
            searchItems = searchPayload;
          } else if (searchPayload && typeof searchPayload === "object") {
            searchItems = searchPayload.brokers || searchPayload.items || searchPayload.list || searchPayload.data || [];
          }

          console.log(`Found ${searchItems.length} brokers via search`);
          broker = searchItems.find((b) => {
            const matches = (b._id === brokerId || b.id === brokerId);
            if (matches) console.log("✅ Broker match found:", b._id || b.id);
            return matches;
          });
          
          if (broker) {
            console.log("✅ Broker found via search filter");
          } else {
            console.log("⚠️ Broker not found in search results");
          }
        } catch (searchErr) {
          console.error("❌ Search filter failed:", searchErr);
        }

        // Strategy 2: Fetch multiple pages systematically
        if (!broker) {
          console.log("Strategy 2: Trying systematic pagination...");
          const maxPages = 20;
          const pageSize = 100;

          for (let page = 1; page <= maxPages; page++) {
            try {
              console.log(`Fetching page ${page}...`);
              const res = await getAllBrokers(
                page,
                pageSize,
                "", // No search filter
                "", // No from date
                "", // No to date
                "", // No block filter
                "", // No status filter
                "false"
              );

              const payload = res?.data ?? res;
              let items = [];
              if (Array.isArray(payload)) {
                items = payload;
              } else if (payload && typeof payload === "object") {
                items = payload.brokers || payload.items || payload.list || payload.data || [];
              }

              console.log(`Page ${page}: ${items.length} brokers`);
              broker = items.find((b) => b._id === brokerId || b.id === brokerId);
              
              if (broker) {
                console.log(`✅ Broker found on page ${page}`);
                break;
              }

              // If we got fewer items than page size, we've reached the end
              if (items.length < pageSize) {
                console.log(`Reached end of data at page ${page}`);
                break;
              }
            } catch (pageErr) {
              console.error(`Error fetching page ${page}:`, pageErr);
              break;
            }
          }
        }

        if (!broker) {
          console.error("❌ Broker not found with ID:", brokerId);
        }

        const properties = broker?.properties || [];
        setListingData(properties);

        // Calculate pages needed for properties
        const pages = Math.ceil(properties.length / perPage);
        setTotalCount(pages > 0 ? pages : 1);
      } catch (err) {
        console.error("❌ Failed to fetch broker:", err);
        setListingData([]);
        setTotalCount(1);
      } finally {
        setLoading(false);
      }
    },
    [brokerId, perPage]
  );

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Local pagination of properties, slice data to show on current page
  const paginatedProperties = listingData.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  // Pagination page change handler
  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row className="mb-3">
          <Col lg="6">
            <h4 className="mb-0">Broker Properties</h4>
          </Col>
          <Col lg="6" className="text-end">
            <Button
             className="btn all-common-btn"
              outline
              onClick={() => history.push(`/admin/brokers/${brokerId}`)}
            >
              Back to Details
            </Button>
          </Col>
        </Row>

        <Card className="card-transparent">
          <div className="card-body">
            {loading ? (
              <div className="d-flex justify-content-center p-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table common-table table-nowrap table-bordered">
                    <thead>
                      <tr>
                        <th>Sr No</th>
                        <th>Address</th>
                        <th>Purpose</th>
                        <th>Owner Type</th>
                        <th>Price</th>
                        <th>Status</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProperties.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No properties found for this broker
                          </td>
                        </tr>
                      ) : (
                        paginatedProperties.map((property, index) => (
                          <tr key={property._id || index}>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>{property.address || "-"}</td>
                            <td className="text-capitalize">{property.property_purpose || "-"}</td>
                            <td className="text-capitalize">{property.ownerType || "-"}</td>
                            <td>
                              $
                              {property.listing_price
                                ? property.listing_price.toLocaleString()
                                : "-"}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  property.status === "active"
                                    ? "bg-success"
                                    : property.status === "draft"
                                    ? "bg-warning"
                                    : "bg-secondary"
                                }`}
                              >
                                {property.status?.toUpperCase() || "-"}
                              </span>
                            </td>
                            <td>{property.created_at ? dateformat(property.created_at) : "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalCount >= 1 && (

                 <Row className="mt-2">
                  <Col md={9}></Col>
                  <Col md={3}>
                    <Pagination
                      {...bootstrap5PaginationPreset}
                      current={currentPage}
                      total={Math.max(1, totalCount)}
                      onPageChange={(page) => handlePageClick(page)}
                      className="pagination justify-content-end gap-1"
                    />
                  </Col>
                </Row>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BrokerProperties;
